import type { PostgrestSingleResponse } from "@supabase/postgrest-js";
import { useSupabaseCallback, useSupabaseLoader } from "../../../../server/supabase/hooks";
import type { SupabaseCallback } from "../../../../server/supabase/hooks/use-supabase-callback";
import type { SmsConfig } from "../server/supabase-sms-config-service";

export function useSmsConfig(): ReturnType<typeof useSupabaseLoader<SmsConfig | null>> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data, error } = await supabase
      .from("sms_config")
      .select("*")
      .eq("account_id", account_id)
      .maybeSingle();

    if (error) throw error;

    return data;
  }, []);
}

export function useUpdateSmsConfig(): SupabaseCallback<
  [{ footer?: SmsConfig["footer"]; prepend_company_name?: SmsConfig["prepend_company_name"] }],
  PostgrestSingleResponse<SmsConfig>
> {
  return useSupabaseCallback(
    async (
      { supabase, account_id },
      {
        footer,
        prepend_company_name,
      }: { footer?: SmsConfig["footer"]; prepend_company_name?: SmsConfig["prepend_company_name"] },
    ) => {
      // Try to get existing config for this account first
      const { data: existingConfig } = await supabase
        .from("sms_config")
        .select("*")
        .eq("account_id", account_id)
        .maybeSingle();

      if (existingConfig) {
        // Update existing config
        return await supabase
          .from("sms_config")
          .update({
            ...(footer !== undefined ? { footer } : {}),
            ...(prepend_company_name !== undefined
              ? { prepend_company_name: prepend_company_name ?? false }
              : {}),
          })
          .eq("id", existingConfig.id)
          .select()
          .single();
      } else {
        // Create new config
        return await supabase
          .from("sms_config")
          .insert({
            account_id,
            footer: footer ?? null,
            prepend_company_name: prepend_company_name ?? false,
          })
          .select()
          .single();
      }
    },
    [],
  );
}
